// src/services/pdfGenerator.js
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Generuje PDF z HTML z obsługą obrazków (w tym podpisów)
 * @param {string} htmlContent - HTML do konwersji
 * @param {Object} options - Opcje generowania
 * @param {boolean} options.singlePage - Czy generować tylko pierwszą stronę
 * @param {Object} options.watermark - Konfiguracja watermarku
 * @param {string} options.watermark.text - Tekst watermarku
 * @param {number} options.watermark.opacity - Przezroczystość (0-1)
 * @param {number} options.watermark.fontSize - Rozmiar czcionki
 * @param {string} options.watermark.color - Kolor tekstu (hex)
 * @param {number} options.watermark.rotation - Kąt obrotu w stopniach
 * @returns {Promise<Blob>} - PDF jako Blob
 */
export const generatePDFFromHTML = async (htmlContent, options = {}) => {
    console.log('🖨️ Rozpoczynam generowanie PDF...');

    // Domyślne opcje
    const defaultOptions = {
        singlePage: false,
        watermark: {
            text: 'KOPIA',
            opacity: 0.1,
            fontSize: 60,
            color: '#cccccc',
            rotation: -45
        }
    };

    // Połącz opcje domyślne z przekazanymi
    const config = {
        ...defaultOptions,
        ...options,
        watermark: {
            ...defaultOptions.watermark,
            ...(options.watermark || {})
        }
    };

    // Utwórz tymczasowy element div
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'fixed';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '0';
    tempDiv.style.width = '180mm'; // A4 width minus marginesy (210mm - 15mm - 15mm)
    tempDiv.style.padding = '0 15mm'; // Dodaj padding dla lepszego renderowania
    tempDiv.style.minHeight = '297mm'; // A4 height
    tempDiv.style.backgroundColor = 'white';
    tempDiv.style.padding = '0';
    tempDiv.style.margin = '0';
    tempDiv.style.boxSizing = 'border-box';

    // Wstaw HTML
    tempDiv.innerHTML = htmlContent;

    // Dodaj do dokumentu
    document.body.appendChild(tempDiv);

    try {
        // Znajdź wszystkie obrazki
        const images = tempDiv.querySelectorAll('img');
        console.log(`🖼️ Znaleziono ${images.length} obrazków do załadowania`);

        // Poczekaj aż wszystkie obrazki się załadują
        const imagePromises = Array.from(images).map(img => {
            return new Promise((resolve, reject) => {
                if (img.complete) {
                    resolve();
                } else {
                    img.onload = resolve;
                    img.onerror = () => {
                        console.warn('⚠️ Nie udało się załadować obrazka:', img.src);
                        resolve(); // Kontynuuj mimo błędu
                    };

                    // Timeout na wypadek gdyby obrazek się nie załadował
                    setTimeout(() => {
                        console.warn('⚠️ Timeout ładowania obrazka:', img.src);
                        resolve();
                    }, 5000);
                }
            });
        });

        await Promise.all(imagePromises);
        console.log('✅ Wszystkie obrazki załadowane');

        // Dodatkowe opóźnienie dla pewności
        await new Promise(resolve => setTimeout(resolve, 500));

        // Opcje dla html2canvas
        const canvas = await html2canvas(tempDiv, {
            scale: 2, // Wysoka jakość
            logging: false,
            useCORS: true, // Ważne dla obrazków z Firebase Storage
            allowTaint: false, // Ważne dla CORS
            backgroundColor: '#ffffff',
            windowWidth: tempDiv.scrollWidth,
            windowHeight: tempDiv.scrollHeight,
            imageTimeout: 15000, // 15 sekund na obrazki
            onclone: (clonedDoc) => {
                // Upewnij się że obrazki w sklonowanym dokumencie też są załadowane
                const clonedImages = clonedDoc.querySelectorAll('img');
                clonedImages.forEach(img => {
                    img.style.display = 'block';
                    img.style.maxWidth = '100%';
                    img.style.height = 'auto';
                });
            }
        });

        console.log('✅ Canvas utworzony:', canvas.width, 'x', canvas.height);

        // Konwertuj do PDF
        const imgData = canvas.toDataURL('image/png', 1.0);

        // Utwórz PDF
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            compress: true
        });

        // Ustaw marginesy (w mm)
        const marginLeft = 30;    // margines lewy
        const marginRight = 30;   // margines prawy  
        const marginTop = 20;     // margines górny
        const marginBottom = 20;  // margines dolny

        // Oblicz wymiary z uwzględnieniem marginesów
        const pageWidth = 210; // A4 width in mm
        const pageHeight = 297; // A4 height in mm
        const contentWidth = pageWidth - marginLeft - marginRight;
        const contentHeight = pageHeight - marginTop - marginBottom;

        // Przeskaluj obraz do obszaru z marginesami
        const imgWidth = contentWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // Oblicz ile stron potrzebujemy
        const totalPages = config.singlePage ? 1 : Math.ceil(imgHeight / contentHeight);

        console.log(`📄 Generowanie ${totalPages} ${totalPages === 1 ? 'strony' : 'stron'}`);

        // Funkcja dodająca watermark
        const addWatermark = (pageNumber) => {
            if (!config.watermark || !config.watermark.text) return;

            // Zapisz stan
            pdf.saveGraphicsState();

            // Ustaw opacity
            pdf.setGState(new pdf.GState({ opacity: config.watermark.opacity }));

            // Ustaw czcionkę
            pdf.setFontSize(config.watermark.fontSize);
            pdf.setTextColor(config.watermark.color);

            // Oblicz pozycję watermarku (środek strony)
            const textWidth = pdf.getTextWidth(config.watermark.text);
            const centerX = pageWidth / 2;
            const centerY = pageHeight / 2;

            // Obróć tekst
            const angle = config.watermark.rotation * Math.PI / 180;
            
            // Dodaj watermark
            pdf.text(config.watermark.text, centerX, centerY, {
                angle: angle,
                align: 'center',
                baseline: 'middle'
            });

            // Opcjonalnie: dodaj numer strony w watermarku
            if (totalPages > 1 && !config.singlePage) {
                pdf.setFontSize(12);
                pdf.text(`Strona ${pageNumber} z ${totalPages}`, pageWidth - marginRight, pageHeight - 10, {
                    align: 'right'
                });
            }

            // Przywróć stan
            pdf.restoreGraphicsState();
        };

        // Dodaj strony z zachowaniem marginesów na każdej stronie
        for (let i = 0; i < totalPages; i++) {
            if (i > 0) {
                pdf.addPage();
            }
            
            // Oblicz który fragment obrazu pokazać na tej stronie
            const sourceY = (i * contentHeight * canvas.height) / imgHeight;
            const sourceHeight = Math.min(
                (contentHeight * canvas.height) / imgHeight,
                canvas.height - sourceY
            );
            
            // Oblicz wysokość fragmentu na stronie PDF
            const fragmentHeight = Math.min(contentHeight, imgHeight - (i * contentHeight));
            
            // Dodaj watermark PRZED dodaniem obrazu (będzie w tle)
            addWatermark(i + 1);
            
            // Dodaj fragment obrazu z zachowaniem marginesów
            pdf.addImage(
                imgData, 
                'PNG', 
                marginLeft,           // Zawsze ten sam margines lewy
                marginTop,            // Zawsze ten sam margines górny
                imgWidth,             // Szerokość z uwzględnieniem marginesów
                fragmentHeight,       // Wysokość fragmentu
                undefined,
                'FAST',
                0,                    // rotacja
                sourceY,              // początek wycinka w obrazie źródłowym (Y)
                canvas.width,         // szerokość wycinka
                sourceHeight          // wysokość wycinka
            );
            
            console.log(`📄 Strona ${i + 1}: Fragment od ${sourceY}px, wysokość ${sourceHeight}px`);
        }

        // Dodaj metadane
        pdf.setProperties({
            title: 'Dokument z podpisem',
            subject: 'Wygenerowany dokument PDF',
            author: 'COLOR PARTNER sp. z o.o.',
            keywords: 'umowa, podpis',
            creator: 'System PartnerPlus'
        });

        console.log('✅ PDF wygenerowany pomyślnie');

        // Zwróć PDF jako Blob
        const pdfBlob = pdf.output('blob');
        console.log('📄 Rozmiar PDF:', (pdfBlob.size / 1024).toFixed(2), 'KB');

        return pdfBlob;

    } catch (error) {
        console.error('❌ Błąd generowania PDF:', error);
        throw error;
    } finally {
        // Usuń tymczasowy element
        try {
            document.body.removeChild(tempDiv);
        } catch (e) {
            console.warn('⚠️ Nie udało się usunąć tymczasowego elementu:', e);
        }
    }
};

/**
 * Generuje PDF z własnym watermarkiem
 * @param {string} htmlContent - HTML do konwersji
 * @param {string} watermarkText - Tekst watermarku
 * @returns {Promise<Blob>} - PDF jako Blob
 */
export const generatePDFWithCustomWatermark = async (htmlContent, watermarkText) => {
    return generatePDFFromHTML(htmlContent, {
        watermark: {
            text: watermarkText,
            opacity: 0.15,
            fontSize: 50,
            color: '#999999',
            rotation: -45
        }
    });
};

/**
 * Generuje tylko pierwszą stronę PDF
 * @param {string} htmlContent - HTML do konwersji
 * @returns {Promise<Blob>} - PDF jako Blob
 */
export const generateSinglePagePDF = async (htmlContent) => {
    return generatePDFFromHTML(htmlContent, {
        singlePage: true,
        watermark: null // Bez watermarku
    });
};

/**
 * Sprawdza czy URL obrazka jest dostępny
 * @param {string} url - URL do sprawdzenia
 * @returns {Promise<boolean>}
 */
export const checkImageAvailability = async (url) => {
    try {
        const response = await fetch(url, {
            method: 'HEAD',
            mode: 'cors'
        });
        return response.ok;
    } catch (error) {
        console.error('❌ Błąd sprawdzania dostępności obrazka:', error);
        return false;
    }
};

/**
 * Konwertuje obrazek na base64
 * @param {string} url - URL obrazka
 * @returns {Promise<string>} - Base64 string
 */
export const imageToBase64 = async (url) => {
    try {
        const response = await fetch(url, {
            mode: 'cors'
        });
        const blob = await response.blob();

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        console.error('❌ Błąd konwersji obrazka na base64:', error);
        throw error;
    }
};

/**
 * Alternatywna metoda - prostsza, bez html2canvas
 * Używaj gdy html2canvas ma problemy z obrazkami
 */
export const generateSimplePDFFromHTML = async (htmlContent) => {
    console.log('🖨️ Generowanie prostego PDF...');

    try {
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        // Tu możesz dodać własną logikę parsowania HTML
        // i dodawania tekstu/obrazków do PDF

        console.log('✅ Prosty PDF wygenerowany');

        return pdf.output('blob');

    } catch (error) {
        console.error('❌ Błąd generowania prostego PDF:', error);
        throw error;
    }
};

// Eksportuj domyślnie główną funkcję
export default generatePDFFromHTML;